import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { SettingsPage, TwoFactorPage } from "../components/index.js";
import { useApiClient } from "../api/ApiContext.jsx";
import { useApi } from "../hooks/useApi.js";
import { useDebouncedCallback } from "../hooks/useDebounced.js";
import { ErrorView, InlineMessage, PageLoading, errorText } from "../ui/Feedback.jsx";

export default function ProjectSettingsScreen() {
  const api = useApiClient();
  const navigate = useNavigate();
  const { projectId } = useParams();

  const detailQuery = useApi(() => api.projects.get(projectId), [api, projectId]);
  const downtimeQuery = useApi(() => api.projects.downtime(projectId), [api, projectId]);

  const [message, setMessage] = useState(null); // { tone, text }
  const [webhookTest, setWebhookTest] = useState(null);
  const [revealOpen, setRevealOpen] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState(null);
  const [addWindowOpen, setAddWindowOpen] = useState(false);

  // SettingsPage fires onUpdateWebhook on every keystroke of the URL field, so
  // the PATCH is debounced instead of one write per character.
  const saveWebhook = useDebouncedCallback(async ({ callbackUrl, mode }) => {
    try {
      await api.projects.update(projectId, { callbackUrl, mode });
      setMessage({ tone: "success", text: "Webhook settings saved." });
    } catch (err) {
      setMessage({ tone: "danger", text: errorText(err, "Could not save the webhook settings.") });
    }
  }, 600);

  async function handleTestWebhook() {
    setMessage(null);
    setWebhookTest(null);
    try {
      const result = await api.projects.testWebhook(projectId);
      setWebhookTest(
        `Test delivery accepted — HTTP ${result?.status_code ?? "?"} after ${result?.attempts ?? 1} attempt(s).`,
      );
    } catch (err) {
      setMessage({
        tone: "danger",
        text:
          err?.code === "upstream_failed"
            ? `Upstream webhook failed${err.details?.length ? `: ${err.details.join(" ")}` : "."}`
            : errorText(err, "Could not test the webhook."),
      });
    }
  }

  async function handleRevealSecret(code) {
    setMessage(null);
    try {
      const result = await api.projects.revealSecret(projectId, code);
      setRevealedSecret(result?.secret ?? "");
      setRevealOpen(false);
    } catch (err) {
      setMessage({
        tone: err?.code === "two_factor_required" ? "warning" : "danger",
        text:
          err?.code === "two_factor_required"
            ? "A second factor is required — enable 2FA on your account, then try again."
            : errorText(err, "Could not reveal the project secret."),
      });
      if (err?.status !== 403) setRevealOpen(false);
    }
  }

  async function handleAddDowntime(window_) {
    setMessage(null);
    try {
      await api.projects.addDowntime(projectId, window_);
      setAddWindowOpen(false);
      downtimeQuery.reload();
    } catch (err) {
      setMessage({ tone: "danger", text: errorText(err, "Could not add the downtime window.") });
    }
  }

  async function handleRemoveDowntime(windowId) {
    setMessage(null);
    try {
      await api.projects.removeDowntime(projectId, windowId);
      downtimeQuery.reload();
    } catch (err) {
      setMessage({ tone: "danger", text: errorText(err, "Could not remove the downtime window.") });
    }
  }

  const plan = useMemo(
    () => detailQuery.data?.plan ?? null,
    [detailQuery.data],
  );

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
        <button type="button" className="ical-btn" onClick={() => navigate(`/projects/${projectId}`)}>
          ← Back to project
        </button>
      </div>

      {message ? <InlineMessage tone={message.tone}>{message.text}</InlineMessage> : null}
      {webhookTest ? <InlineMessage tone="success">{webhookTest}</InlineMessage> : null}
      {revealedSecret ? (
        <InlineMessage tone="info" style={{ fontFamily: "var(--font-mono)" }}>
          Project secret: {revealedSecret} — store it now; it will not be shown again.
        </InlineMessage>
      ) : null}

      {revealOpen ? (
        <div className="ical-card" style={{ marginBottom: 20 }}>
          <TwoFactorPage
            destination="your authenticator app"
            onVerify={handleRevealSecret}
            onResend={() =>
              setMessage({ tone: "warning", text: "Request a fresh code from your authenticator app." })
            }
          />
          <div style={{ textAlign: "center" }}>
            <button type="button" className="ical-btn" onClick={() => setRevealOpen(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {addWindowOpen ? (
        <DowntimeForm onSubmit={handleAddDowntime} onCancel={() => setAddWindowOpen(false)} />
      ) : null}

      {detailQuery.loading || downtimeQuery.loading ? <PageLoading /> : null}

      {detailQuery.error || downtimeQuery.error ? (
        <div>
          <InlineMessage tone="danger">{errorText(detailQuery.error || downtimeQuery.error)}</InlineMessage>
          <ErrorView
            error={detailQuery.error || downtimeQuery.error}
            onRetry={() => {
              detailQuery.reload();
              downtimeQuery.reload();
            }}
            onBack={() => navigate("/")}
          />
        </div>
      ) : null}

      {!detailQuery.loading && !detailQuery.error && !downtimeQuery.loading && !downtimeQuery.error ? (
        <SettingsPage
          projectId={projectId}
          projectStatus={detailQuery.data.status}
          apiKey={detailQuery.data.apiKey}
          onRevealSecret={() => setRevealOpen(true)}
          webhook={detailQuery.data.webhook}
          onTestWebhook={handleTestWebhook}
          onUpdateWebhook={saveWebhook}
          downtimeWindows={downtimeQuery.data.windows}
          onAddDowntime={() => setAddWindowOpen(true)}
          onRemoveDowntime={handleRemoveDowntime}
          plan={plan}
          onChangePlan={() => navigate("/billing")}
        />
      ) : null}
    </div>
  );
}

/** Small local form: the contract takes {starts_at, ends_at, label} but no
 *  component owns that input, so it lives beside the screen. */
function DowntimeForm({ onSubmit, onCancel }) {
  const [label, setLabel] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");

  function submit(event) {
    event.preventDefault();
    if (!startsAt || !endsAt) return;
    onSubmit({
      label,
      startsAt: new Date(startsAt).toISOString(),
      endsAt: new Date(endsAt).toISOString(),
    });
  }

  return (
    <form className="ical-card" style={{ marginBottom: 20 }} onSubmit={submit}>
      <p style={{ margin: "0 0 10px", fontSize: "var(--text-sm)", fontWeight: 500 }}>
        Add downtime window
      </p>
      <label style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)", display: "block", marginBottom: 4 }}>
        Label
      </label>
      <input
        className="ical-input"
        required
        style={{ width: "100%", marginBottom: 10 }}
        value={label}
        onChange={(event) => setLabel(event.target.value)}
        placeholder="Monthly maintenance"
      />
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)", display: "block", marginBottom: 4 }}>
            Starts (local)
          </label>
          <input
            className="ical-input"
            type="datetime-local"
            required
            style={{ width: "100%" }}
            value={startsAt}
            onChange={(event) => setStartsAt(event.target.value)}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)", display: "block", marginBottom: 4 }}>
            Ends (local)
          </label>
          <input
            className="ical-input"
            type="datetime-local"
            required
            style={{ width: "100%" }}
            value={endsAt}
            onChange={(event) => setEndsAt(event.target.value)}
          />
        </div>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button type="submit" className="ical-btn ical-btn-primary">
          Add window
        </button>
        <button type="button" className="ical-btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
